/**
 * Token 管理器
 * 负责用户认证令牌的存储、获取、更新和状态管理
 * @author 杨俊朗
 */
import { canUseLocalStorage } from "@/utils/canuseLocalStorage";
import { canUseWindow } from "@/utils/canuseWindow";
import { ENV } from "@/constant";
import { imaCookieManager } from "./ima-cookie-manager";

// 事件类型定义
type TokenEvent = CustomEvent<{
  token: string;
  timestamp: number;
}>;

// Token 状态接口
interface TokenState {
  token: string;
  lastUpdateTime: number;
}

export class IamTokenManager {
  private static instance: IamTokenManager;
  private readonly STORAGE_KEY = "MIUTA_TOKEN";
  private readonly EVENT_KEY = "MIUTA_TOKEN_CHANGE";

  // Token 状态
  private tokenState: TokenState = {
    token: "",
    lastUpdateTime: 0
  };

  private constructor() {
    this.initializeFromStorage();
  }

  public static getInstance(): IamTokenManager {
    if (!IamTokenManager.instance) {
      IamTokenManager.instance = new IamTokenManager();
    }
    return IamTokenManager.instance;
  }

  // ==================== Token 管理 ====================

  /**
   * 获取当前 Token
   * @returns 当前存储的 Token 字符串
   */
  public getToken(): string {
    if (!this.tokenState.token) {
      this.syncTokenFromStorage();
    }
    return this.tokenState.token;
  }

  /**
   * 更新 Token
   * @param token 新的 Token 值
   */
  public updateToken(token: string): void {
    this.tokenState = {
      token: token || "",
      lastUpdateTime: Date.now()
    };

    this.saveToStorage();
    this.dispatch();
  }

  /**
   * 清除 Token
   */
  public clear(): void {
    this.updateToken("");
    // 清除 Cookie
    imaCookieManager.clear();
  }

  // ==================== 事件管理 ====================

  /**
   * 添加 Token 状态变化监听器
   * @param listener 监听器函数
   * @returns 移除监听器的函数
   */
  public listen(listener: (e: TokenEvent) => void): () => void {
    if (!canUseWindow()) {
      return () => {};
    }

    window.addEventListener(this.EVENT_KEY, listener);

    return () => {
      window.removeEventListener(this.EVENT_KEY, listener);
    };
  }

  /**
   * 重置 Token 管理器状态
   */
  public reset(): void {
    this.tokenState = {
      token: "",
      lastUpdateTime: 0
    };
    this.saveToStorage();
    this.dispatch();
  }

  // ==================== 私有方法 ====================

  /**
   * 从本地存储初始化 Token
   */
  private initializeFromStorage(): void {
    this.syncTokenFromStorage();
  }

  /**
   * 从 localStorage / Cookie 同步 Token 到内存和缓存
   * 解决初始化时 Cookie 尚未可用导致的漏读问题
   */
  private syncTokenFromStorage(): void {
    if (!canUseLocalStorage()) {
      return;
    }

    try {
      const localToken = localStorage.getItem(this.STORAGE_KEY);
      const shouldSkipCookieFallback = ENV.isProdMainEnv || ENV.isTestMainEnv;
      // 线上环境主站, 测试环境主站 不从 Cookie 获取 token (只有定制域名才需要)
      const storedToken = localToken || (shouldSkipCookieFallback ? "" : imaCookieManager.get());
      if (storedToken) {
        this.tokenState = {
          token: storedToken,
          lastUpdateTime: Date.now()
        };
        this.saveToStorage();
      }
    } catch (error) {
      console.error("Failed to initialize token from localStorage:", error);
      this.tokenState = {
        token: "",
        lastUpdateTime: 0
      };
      imaCookieManager.clear();
    }
  }

  /**
   * 保存 Token 到本地存储
   */
  private saveToStorage(): void {
    if (!canUseLocalStorage()) {
      return;
    }

    try {
      if (this.tokenState.token) {
        localStorage.setItem(this.STORAGE_KEY, this.tokenState.token);
        imaCookieManager.set(this.tokenState.token);
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
        imaCookieManager.clear();
      }
    } catch (error) {
      console.error("Failed to save token to localStorage:", error);
    }
  }

  /**
   * 触发 Token 状态变化事件
   */
  private dispatch(): void {
    if (!canUseWindow()) {
      return;
    }

    const event = new CustomEvent<{
      token: string;
      timestamp: number;
    }>(this.EVENT_KEY, {
      detail: {
        token: this.tokenState.token,
        timestamp: this.tokenState.lastUpdateTime
      }
    });

    window.dispatchEvent(event);
  }
}

// 创建单例实例
export const iamTokenManager = IamTokenManager.getInstance();
